/**
 * The browser's socket to the composer's dictation (`/v1/agent/dictate/live`).
 *
 * The phone's `dictation-socket.ts`, opened as the browser must: no header
 * argument, so the tenant rides the query (`withTenant`) and the cookie
 * travels by itself. Everything else is the phone's: the path
 * (`dictationWsUrl`), the reader (`readDictationServerMessage`), the handler
 * and socket shapes, `start` first whichever of the socket opening or the
 * first microphone frame gets there first, and the frames recorded while the
 * socket opens kept and sent right after `start`, so the first word is not
 * lost to the handshake.
 */
import type { AgentLanguageTag, DictationClientMessage } from '@alluzo/shared';

import { apiBaseUrl } from '@/lib/api';
import { DICTATION_EARLY_FRAMES, type DictationHandlers, type DictationSocket } from '@/lib/dictation-socket';
import { dictationWsUrl, readDictationServerMessage } from '@/lib/dictation-wire';

import { withTenant } from './live-urls';

export type { DictationHandlers, DictationSocket };

/** The dictation's URL, always inside one business. */
export const dictationUrl = (tenantId: string): string => withTenant(dictationWsUrl(apiBaseUrl), tenantId);

export const connectDictation = (tenantId: string, language: AgentLanguageTag, handlers: DictationHandlers): DictationSocket => {
  const ws = new WebSocket(dictationUrl(tenantId));
  ws.binaryType = 'arraybuffer';

  const send = (message: DictationClientMessage): void => {
    if (ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify(message));
  };

  const early: Uint8Array[] = [];
  let started = false;
  const sendStart = (): void => {
    if (started || ws.readyState !== WebSocket.OPEN) return;
    send({ type: 'start', language });
    started = true;
    for (const pcm of early.splice(0)) ws.send(pcm);
  };

  ws.onopen = () => sendStart();
  ws.onmessage = (event: MessageEvent<unknown>) => {
    if (event.data instanceof ArrayBuffer) return;
    const message = readDictationServerMessage(event.data);
    if (message !== null) handlers.onMessage(message);
  };
  ws.onclose = (event) => handlers.onClose(event.code, event.reason);
  ws.onerror = () => handlers.onError();

  const close = (): void => {
    // The handlers belong to a dictation that is over.
    ws.onopen = null;
    ws.onclose = null;
    ws.onerror = null;
    ws.onmessage = null;
    early.length = 0;
    ws.close();
  };

  return {
    sendAudio: (pcm) => {
      if (ws.readyState === WebSocket.OPEN) {
        sendStart();
        ws.send(pcm);
        return;
      }
      if (ws.readyState === WebSocket.CONNECTING && early.length < DICTATION_EARLY_FRAMES) early.push(pcm);
    },
    finish: () => send({ type: 'finish' }),
    cancel: () => {
      send({ type: 'cancel' });
      close();
    },
    close,
  };
};
